import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Timestamp, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/firebase';

type TesttypeSummary = {
  paper: number;
  online: number;
  unset: number;
  total: number;
};

type PeriodMode = 'all' | 'custom';

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const MIN_TARGET_UTC_MS = Date.UTC(2025, 11, 1, 3, 0, 0); // 2025/12/1 12:00:00 JST

const pad2 = (value: number) => value.toString().padStart(2, '0');

const toJstYmd = (millis: number) => {
  const jstDate = new Date(millis + JST_OFFSET_MS);
  return `${jstDate.getUTCFullYear()}-${pad2(jstDate.getUTCMonth() + 1)}-${pad2(jstDate.getUTCDate())}`;
};

const parseYmd = (value: string) => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

const getUtcMillisFromJstYmdStart = (value: string) => {
  const parsed = parseYmd(value);
  if (!parsed) return null;
  return Date.UTC(parsed.year, parsed.month - 1, parsed.day) - JST_OFFSET_MS;
};

export default function OnlineTestSummaryPage() {
  const [summary, setSummary] = useState<TesttypeSummary>({ paper: 0, online: 0, unset: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodMode, setPeriodMode] = useState<PeriodMode>('all');
  const [startDate, setStartDate] = useState<string>(() => toJstYmd(MIN_TARGET_UTC_MS));
  const [endDate, setEndDate] = useState<string>(() => toJstYmd(Date.now()));

  const customRangeError = useMemo(() => {
    if (periodMode !== 'custom') return null;
    if (!startDate || !endDate) return 'カスタム期間では開始日と終了日の入力が必要です。';
    const startMillis = getUtcMillisFromJstYmdStart(startDate);
    const endStartMillis = getUtcMillisFromJstYmdStart(endDate);
    if (startMillis === null || endStartMillis === null) return '日付形式が不正です。';
    if (startMillis > endStartMillis) return '開始日は終了日以前を指定してください。';
    return null;
  }, [endDate, periodMode, startDate]);

  useEffect(() => {
    const fetchSummary = async () => {
      if (customRangeError) {
        setSummary({ paper: 0, online: 0, unset: 0, total: 0 });
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        let startMillis = MIN_TARGET_UTC_MS;
        let endExclusiveMillis: number | null = null;

        if (periodMode === 'custom') {
          const customStartMillis = getUtcMillisFromJstYmdStart(startDate);
          const customEndStartMillis = getUtcMillisFromJstYmdStart(endDate);
          if (customStartMillis === null || customEndStartMillis === null) {
            throw new Error('日付形式が不正です。');
          }
          startMillis = Math.max(MIN_TARGET_UTC_MS, customStartMillis);
          endExclusiveMillis = customEndStartMillis + ONE_DAY_MS;
        }

        const orderRef = collection(db, 'Order');
        const orderQuery =
          endExclusiveMillis === null
            ? query(orderRef, where('createdAt', '>=', Timestamp.fromMillis(startMillis)))
            : query(
                orderRef,
                where('createdAt', '>=', Timestamp.fromMillis(startMillis)),
                where('createdAt', '<', Timestamp.fromMillis(endExclusiveMillis)),
              );

        const snapshot = await getDocs(orderQuery);
        let paperTotal = 0;
        let onlineTotal = 0;
        let unsetTotal = 0;

        snapshot.docs.forEach((docSnapshot) => {
          const rawType = (docSnapshot.data() as { testtype?: unknown }).testtype;
          if (rawType === 'paper') {
            paperTotal += 1;
            return;
          }
          if (rawType === 'online') {
            onlineTotal += 1;
            return;
          }
          unsetTotal += 1;
        });

        setSummary({
          paper: paperTotal,
          online: onlineTotal,
          unset: unsetTotal,
          total: paperTotal + onlineTotal + unsetTotal,
        });
      } catch (fetchError) {
        console.error('Error fetching online test summary:', fetchError);
        setError('集計の取得に失敗しました。時間をおいて再度お試しください。');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [customRangeError, endDate, periodMode, startDate]);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">オンラインテスト集計</h1>
        <Link
          to="/admin/answers"
          className="inline-flex items-center rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
        >
          テスト一覧
        </Link>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700">集計期間</span>
          <button
            type="button"
            onClick={() => setPeriodMode('all')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              periodMode === 'all' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            全期間
          </button>
          <button
            type="button"
            onClick={() => setPeriodMode('custom')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              periodMode === 'custom' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            カスタム期間
          </button>
        </div>

        {periodMode === 'custom' && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="flex flex-col text-sm text-gray-600">
              開始日（JST）
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800"
              />
            </label>
            <label className="flex flex-col text-sm text-gray-600">
              終了日（JST）
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800"
              />
            </label>
          </div>
        )}

        <p className="text-xs text-gray-500">
          createdAtが2025/12/1 12:00:00（UTC+9）以降のOrderを対象に集計しています。
        </p>
        {customRangeError && <p className="text-sm text-rose-600">{customRangeError}</p>}
        {error && <p className="text-sm text-rose-600">{error}</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="text-sm text-gray-500">paper（用紙）</div>
          <div className="mt-2 text-3xl font-bold text-gray-800">{loading ? '...' : summary.paper}</div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="text-sm text-gray-500">online（オンライン）</div>
          <div className="mt-2 text-3xl font-bold text-gray-800">{loading ? '...' : summary.online}</div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="text-sm text-gray-500">未設定（null含む）</div>
          <div className="mt-2 text-3xl font-bold text-gray-800">{loading ? '...' : summary.unset}</div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="text-sm text-gray-500">対象Order総数</div>
          <div className="mt-2 text-3xl font-bold text-gray-800">{loading ? '...' : summary.total}</div>
        </div>
      </div>
    </div>
  );
}
